<template>
    <form>
        <!-- Search Input -->
        <div class="input-group flex-nowrap">
            <button
                @click.prevent="fetchAthleteTricks"
                :class="['input-group-text',{ 'active-button': isActive }]"
                :disabled="isFetchingAthleteTricks"
                id="addon-wrapping">
                <font-awesome-icon :icon="['fas', 'magnifying-glass']"/>
            </button>
            <input
                ref="searchFocus"
                v-model="selectedTrick"
                @input="filterTricks"
                @focus="handleFocus"
                @blur="hideResults"
                type="text"
                class="form-control"
                :placeholder="$t('judging_program.trick_selector_box.placeholder')"
                aria-label="Type or select a trick"
                aria-describedby="addon-wrapping"/>
            <!-- Rotation Selector -->
            <input type="hidden" v-model="selectedOptionRotation" name="selectedOption" />
            <button class="btn btn-outline-secondary" :class="{ 'active': selectedOptionRotation === 1 }" :disabled="isValidateSelectButtonDisabled" @click.prevent="handleButtonClick(1)">FS</button>
            <button class="btn btn-outline-secondary" :class="{ 'active': selectedOptionRotation === 2 }" :disabled="isValidateSelectButtonDisabled" @click.prevent="handleButtonClick(2)">BS</button>
        </div>
        <!-- Result Box -->
        <div
            v-show="showResults"
            class="search-results"
            :style="{ minHeight: dropdownMinHeight + 'px' }"
        >
            <div v-if="showEmptyAthleteMessage" class="search-results-empty">
                {{ emptyTricksMessage }}
            </div>
            <div v-for="trick in filteredTricks" :key="trick.id" @click="selectTrick(trick)">
                <div class="container-fluid">
                    <div class="row">
                        <div class="col-1 bg-primary d-flex align-items-center bg-secondary bg-gradient bg-opacity-25  rounded-start-pill mb-1 ms-0 me-0">
                            <div class="level-circle" :class="getBackgroundColorClass(trick.level_id)">
                                <b class="text-white text-sm-general">{{ trick.level_id }}</b>
                            </div>
                        </div>
                        <div class="col-9 bg-secondary bg-gradient bg-opacity-25  rounded-0 mb-1 ms-0 me-0">
                            <div class="row">
                                <div class="col-12 text-start p-1 pb-0 mb-0">
                                    <p class="mb-0 text-sm-title" >
                                        <strong>{{ trick.name }}</strong>
                                    </p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 text-start p-1 pt-0 mt-0">
                                    <p class="m-0 p-0 text-secondary text-sm-general">{{ trick.short_name ? trick.short_name : '&nbsp;' }}</p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-6">
                                    <div class="rounded-pill border border-secondary-subtle text-center" style="background-color: rgb(37,102,199,0.3)">
                                        <p class="m-0 px-1 text-sm-alerts"><em>{{ getCategoryName(trick.categorie_id) }}</em></p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-2 d-flex justify-content-center align-items-center bg-success bg-gradient bg-opacity-50  rounded-end-pill mb-1 ms-0 me-0">
                            <div class="text-center text-sm-score">
                                <strong class="p-1">{{ parseFloat(trick.value.toFixed(2)) }}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Score Buttons -->
        <div v-if="showControls" class="score-buttons">
            <div class="row">
                <div class="col-6 mb-2">
                    <!-- Grab Selector -->
                    <select v-model="selectedGrab" class="form-select" :disabled="isValidateSelectButtonDisabled">
                        <option value="">Grab</option>
                        <option v-for="grab in grabs" :key="grab.id" :value="grab.id">{{ grab.name }}</option>
                    </select>
                </div>
                <div class="col-6  mb-2">
                    <!-- Dub Selector -->
                    <select v-model="selectedDub" class="form-select" :disabled="isValidateSelectButtonDisabled">
                        <option value="">Dub</option>
                        <option v-for="dub in dubs" :key="dub.id" :value="dub.id">{{ dub.name }}</option>
                    </select>
                </div>
                <div class="col-12 mb-2">
                    <!-- Button Selector -->
                    <template v-if="trickEdit">
                        <div class="d-flex flex-wrap gap-2 align-items-center">
                            <button
                                type="button"
                                class="btn btn-secondary flex-fill"
                                @click.stop="cancelSelectedTrickEdit">
                                {{ $t('judging_program.totals_tricks_box.cancel') || $t('general.cancel') || 'Cancelar' }}
                            </button>
                            <button
                                type="button"
                                class="btn btn-warning flex-fill"
                                style="position: relative;"
                                @click.stop="editToSelectedTricks(trickEditIndex)"
                                :class="{'disabled-btn': isValidateSelectButtonDisabled }"
                                :disabled="isValidateSelectButtonDisabled">
                                    <span :class="{ 'disabled-icon': isValidateSelectButtonDisabled }" class="check-icon">
                                        {{ $t('judging_program.trick_selector_box.save_btn') }}
                                        <i v-if="isValidateSelectButtonDisabled" class="fas fa-ban prohibited-icon position-absolute top-0"></i>
                                    </span>
                            </button>
                        </div>
                    </template>
                    <template v-else-if="trickValidatedEdit">
                        <div class="d-flex flex-wrap gap-2 align-items-center">
                            <button
                                type="button"
                                class="btn btn-secondary flex-fill"
                                @click.stop="cancelSelectedTrickEdit">
                                {{ $t('judging_program.totals_tricks_box.cancel') || $t('general.cancel') || 'Cancelar' }}
                            </button>
                            <button
                                type="button"
                                class="btn btn-warning flex-fill"
                                style="position: relative;"
                                @click.stop="editToValidatedTricks(trickValidatedEditIndex,trickValidatedEditSubIndex)"
                                :class="{'disabled-btn': isValidateSelectButtonDisabled }"
                                :disabled="isValidateSelectButtonDisabled">
                                    <span :class="{ 'disabled-icon': isValidateSelectButtonDisabled }" class="check-icon">
                                        {{ $t('judging_program.trick_selector_box.save_btn') }}
                                        <i v-if="isValidateSelectButtonDisabled" class="fas fa-ban prohibited-icon position-absolute top-0"></i>
                                    </span>
                            </button>
                        </div>
                    </template>
                    <button
                        v-else
                        type="button"
                        class="btn btn-success"
                        style="position: relative; width: 100%"
                        @click.stop="addToSelectedTricks"
                        :class="{'disabled-btn': isValidateSelectButtonDisabled }"
                        :disabled="isValidateSelectButtonDisabled">
                            <span :class="{ 'disabled-icon': isValidateSelectButtonDisabled }" class="check-icon">
                                {{ $t('judging_program.trick_selector_box.select_btn') }}
                                <i v-if="isValidateSelectButtonDisabled" class="fas fa-ban prohibited-icon position-absolute top-0"></i>
                            </span>

                    </button>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
export default {
    name: "SearchBox",
    emits: ['update-selected-tricks', 'update-validated-tricks', 'cancel-trick-edit'],
    data() {
        return {
            /*Search Input*/
            isActive: false,
            selectedTrickId: null,
            selectedTrick: null,
            selectedOptionRotation: 0,
            /*Result Box*/
            showResults: false,
            filteredTricks: [],
            athleteTricks: [],
            athleteTricksLoaded: false,
            isAthleteResultsMode: false,
            isFetchingAthleteTricks: false,
            fetchError: '',
            dropdownMinHeight: 520,
            /*Score Buttons*/
            selectedRotation: '',
            selectedGrab: '',
            selectedDub: '',
            categories : [
                { id: 1, name: 'Aerial'},
                { id: 2, name: 'Aerial / Flip'},
                { id: 3, name: 'Aerial / Switch'},
                { id: 4, name: 'Dynamic'},
                { id: 5, name: 'Dynamic / Flip'},
                { id: 6, name: 'Dynamic / Flip / Switch '},
                { id: 7, name: 'Dynamic / Switch'},
                { id: 8, name: 'Dynamic / Transition'},
                { id: 9, name: 'Mount'},
                { id: 10, name: 'Static'},
                { id: 11, name: 'Transition'},
            ],
        };
    },
    props: {
        tricks: Array,
        athleteId: [Number, String],
        token: String,
        selectedListTricks: Array,
        validatedTricks: Array,
        trickEdit: Object,
        trickEditIndex: Number,
        trickValidatedEdit: Object,
        trickValidatedEditIndex: Number,
        trickValidatedEditSubIndex: Number,
    },
    computed: {
        currentTrick() {
            const trickId = parseInt(this.selectedTrickId);
            return this.availableTricks.find(trick => trick.id === trickId) || this.tricks.find(trick => trick.id === trickId);
        },
        availableTricks() {
            if (this.athleteTricksLoaded) {
                return this.athleteTricks || [];
            }
            return this.tricks || [];
        },
        showControls() {
            return Boolean(this.selectedTrickId);
        },
        showEmptyAthleteMessage() {
            return this.isAthleteResultsMode && !this.filteredTricks.length;
        },
        emptyTricksMessage() {
            return this.$t('judging_program.trick_selector_box.empty_athlete_tricks') || 'No tricks are available in the system for this athlete yet.';
        },
        grabs() {
            // Replace this with your grab options
            return [
                {id: 1, name: 'Blunt', value: '1'},
                {id: 2, name: 'Boneless', value: '2'},
                {id: 3, name: 'BuddhaGrab', value: '3'},
                {id: 4, name: 'CanOpener', value: '4'},
                {id: 5, name: 'ChristAir', value: '5'},
                {id: 6, name: 'Crossover', value: '6'},
                {id: 7, name: 'CrossoverSafety', value: '7'},
                {id: 8, name: 'CrossoverTruckDriver', value: '8'},
                {id: 9, name: 'Flat', value: '9'},
                {id: 10, name: 'HalfSquirrelLef.', value: '10'},
                {id: 11, name: 'HalfSquirrelRig.', value: '11'},
                {id: 12, name: 'HighChair', value: '12'},
                {id: 13, name: 'HorizontalTwist', value: '13'},
                {id: 14, name: 'Japan', value: '14'},
                {id: 15, name: 'KneelAir', value: '15'},
                {id: 16, name: 'Layout', value: '16'},
                {id: 17, name: 'Mute', value: '17'},
                {id: 18, name: 'No grab', value: '18'},
                {id: 19, name: 'Open', value: '19'},
                {id: 20, name: 'Pike', value: '20'},
                {id: 21, name: 'SafetyGrab', value: '21'},
                {id: 22, name: 'Scratch', value: '22'},
                {id: 23, name: 'Seatbelt', value: '23'},
                {id: 24, name: 'Shifty', value: '24'},
                {id: 25, name: 'SpreadEagle', value: '25'},
                {id: 26, name: 'SquirrelGrab', value: '26'},
                {id: 27, name: 'Superman', value: '27'},
                {id: 28, name: 'TruckDriver', value: '28'},
                {id: 29, name: 'Tuck', value: '29'},
                {id: 30, name: 'TuckBlunt', value: '30'},
                {id: 31, name: 'TuckedTwist', value: '31'},
                {id: 32, name: 'Twist', value: '32'},
                {id: 33, name: 'Yokohama', value: '33'},
            ];
        },
        dubs() {
            // Replace this with your dub options
            return [
                {id: 1, name: 'Dub 20%', value: 1},
                {id: 2, name: 'Dub 50%', value: 2},
                {id: 3, name: 'Dub 80%', value: 3},
            ];
        },
        isValidateSelectButtonDisabled() {
            const lastTrick = this.selectedListTricks[this.selectedListTricks.length - 1];
            return (lastTrick && lastTrick.dub === 3) || !this.selectedTrick;
        },
    },
    methods: {
        /*Search Input*/
        async fetchAthleteTricks() {
            if (this.isFetchingAthleteTricks) {
                return;
            }

            // Toggle off: close list if currently visible
            if (this.showResults) {
                this.showResults = false;
                this.isActive = false;
                return;
            }

            // Fallback to local list when missing auth context
            if (!this.athleteId || !this.token) {
                this.isAthleteResultsMode = false;
                this.isActive = true;
                this.showResults = true;
                this.filteredTricks = this.tricks || [];
                this.$nextTick(this.updateDropdownHeight);
                return;
            }

            this.isActive = true;
            this.showResults = false;
            this.isAthleteResultsMode = true;
            this.isFetchingAthleteTricks = true;
            this.athleteTricksLoaded = false;
            this.fetchError = '';

            try {
                const { data } = await this.$axios.get(`/athletes/${this.athleteId}/competition-tricks`, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                });

                const incoming = Array.isArray(data?.tricks) ? data.tricks : [];
                const mapped = incoming
                    .map((trick) => this.mapAthleteTrick(trick))
                    .filter(Boolean);

                this.athleteTricks = mapped;
                this.athleteTricksLoaded = true;
                this.filteredTricks = mapped;
                this.showResults = true;
                this.$nextTick(this.updateDropdownHeight);
            } catch (error) {
                console.error('No se pudieron obtener los trucos del atleta', error);
                this.fetchError = error?.response?.data?.message || error?.message || '';
                this.athleteTricks = [];
                this.athleteTricksLoaded = true;
                this.filteredTricks = [];
                this.showResults = true;
                this.$nextTick(this.updateDropdownHeight);
            } finally {
                this.isFetchingAthleteTricks = false;
            }
        },
        filterTricks() {
            const query = (this.selectedTrick || '').toLowerCase().trim();
            const source = this.tricks || [];
            this.isAthleteResultsMode = false;

            // Si el texto ya no coincide con el truco elegido, limpia la selección para ocultar controles
            if (this.selectedTrickId) {
                const selected = this.availableTricks.find((t) => t.id === parseInt(this.selectedTrickId));
                const selectedName = (selected?.name || '').toLowerCase().trim();
                if (!selected || query !== selectedName) {
                    this.selectedTrickId = null;
                    this.selectedOptionRotation = 0;
                    this.selectedGrab = '';
                    this.selectedDub = '';
                }
            }

            if (!query) {
                this.selectedTrickId = null; // limpiar selección para ocultar controles
                this.selectedOptionRotation = 0;
                this.selectedGrab = '';
                this.selectedDub = '';
                this.filteredTricks = source;
                this.showResults = true;
                this.$nextTick(this.updateDropdownHeight);
                return;
            }

            this.filteredTricks = source.filter((trick) => {
                const name = (trick.name || '').toLowerCase();
                const shortName = (trick.short_name || '').toLowerCase();
                const idStr = trick.id !== undefined && trick.id !== null ? String(trick.id).toLowerCase() : '';
                return name.includes(query) || shortName.includes(query) || idStr.includes(query);
            });
            this.showResults = true;
            this.$nextTick(this.updateDropdownHeight);
        },
        hideResults() {
            setTimeout(() => {
                this.showResults = false;
                this.isActive = false;
            }, 200);
        },
        handleButtonClick(option) {
            if (this.selectedOptionRotation === option) {
                this.selectedOptionRotation = 0;
            } else {
                this.selectedOptionRotation = option;
            }
        },
        focus() {
            this.$refs.searchFocus.focus();
        },
        handleFocus() {
            this.selectedOptionRotation = 0;
            const source = this.tricks || [];
            this.isAthleteResultsMode = false;
            this.filteredTricks = source;
            this.showResults = true;
            this.isActive = false; // no activar el botón al enfocar
            this.$nextTick(this.updateDropdownHeight);
        },
        /*Result Box*/
        selectTrick(trick) {
            if (trick) {
                const resolvedTrick = this.availableTricks.find(t => t.id === trick.id) || trick;
                this.selectedTrick = resolvedTrick.name;
                this.selectedTrickId = resolvedTrick.id;
                this.showResults = false;
                this.isActive = false;
            } else {
                console.error('No trick selected');
            }
        },
        mapAthleteTrick(apiTrick) {
            if (!apiTrick || !apiTrick.trick_id) {
                return null;
            }

            const base = this.tricks.find((trick) => trick.id === apiTrick.trick_id) || {};

            return {
                ...base,
                id: apiTrick.trick_id,
                name: apiTrick.name || base.name || '',
                short_name: apiTrick.short_name ?? base.short_name ?? '',
                categorie_id: base.categorie_id ?? null,
                type_id: base.type_id ?? null,
                level_id: base.level_id ?? null,
                creator: base.creator ?? null,
                value: base.value ?? 0,
            };
        },
        getBackgroundColorClass(level) {
            switch (level) {
                case 1:
                    return 'color-level-1';
                case 2:
                    return 'color-level-2';
                case 3:
                    return 'color-level-3';
                case 4:
                    return 'color-level-4';
                default:
                    return '';
            }
        },
        getCategoryName(id) {
            const category = this.categories.find((c) => c.id === id);
            return category ? category.name : '';
        },
        /*Score Buttons*/
        addToSelectedTricks() {
            if (this.currentTrick) {
                const selectedGrabValue = this.getSelectedOptionValue(this.selectedGrab, this.grabs);
                const selectedDubValue = this.getSelectedOptionValue(this.selectedDub, this.dubs);
                const selectedRotationValue = this.selectedOptionRotation;
                this.saveSelectedTrick(this.currentTrick, selectedRotationValue, selectedGrabValue, selectedDubValue);
                this.resetSelectedVariables();
            } else {
                console.error('Current trick not found');
            }
        },
        editToSelectedTricks(trickEditIndex) {
            if (this.currentTrick && trickEditIndex >= 0) {
                const selectedGrabValue = this.getSelectedOptionValue(this.selectedGrab, this.grabs);
                const selectedDubValue = this.getSelectedOptionValue(this.selectedDub, this.dubs);
                const selectedRotationValue = this.selectedOptionRotation;
                this.editSelectedTrick(trickEditIndex,this.currentTrick, selectedRotationValue, selectedGrabValue, selectedDubValue);
                this.resetSelectedVariables();
            } else {
                console.error('Current trick not found');
            }
        },
        editToValidatedTricks(trickValidatedEditIndex,trickValidatedEditSubIndex) {
            if (this.currentTrick && trickValidatedEditIndex >= 0) {
                const selectedGrabValue = this.getSelectedOptionValue(this.selectedGrab, this.grabs);
                const selectedDubValue = this.getSelectedOptionValue(this.selectedDub, this.dubs);
                const selectedRotationValue = this.selectedOptionRotation;
                this.editValidatedTrick(trickValidatedEditIndex,trickValidatedEditSubIndex,this.currentTrick, selectedRotationValue, selectedGrabValue, selectedDubValue);
                this.resetSelectedVariables();
            } else {
                console.error('Current trick not found');
            }
        },
        getSelectedOptionValue(selectedOption, options) {
            const selected = options.find(option => option.id === parseInt(selectedOption));
            return selected ? selected.value : 0;
        },
        saveSelectedTrick(currentTrick, selectedRotationValue, selectedGrabValue, selectedDubValue) {
            const newSelectedTrick = {
                id: currentTrick.id,
                name: currentTrick.name,
                short_name: currentTrick.short_name,
                category: currentTrick.categorie_id,
                type: currentTrick.type_id,
                level: currentTrick.level_id,
                creator: currentTrick.creator,
                value: currentTrick.value,
                variation: selectedRotationValue,
                grab: parseInt(selectedGrabValue),
                dub: selectedDubValue,
                score: currentTrick.value
            };
            this.$emit('update-selected-tricks',null, newSelectedTrick);
        },
        editSelectedTrick(trickEditIndex,currentTrick, selectedRotationValue, selectedGrabValue, selectedDubValue) {
            let trickForEdit = this.selectedListTricks[trickEditIndex];
            if (trickForEdit){
                trickForEdit = {
                    id: currentTrick.id,
                    name: currentTrick.name,
                    short_name: currentTrick.short_name,
                    category: currentTrick.categorie_id,
                    type: currentTrick.type_id,
                    level: currentTrick.level_id,
                    creator: currentTrick.creator,
                    value: currentTrick.value,
                    variation: selectedRotationValue,
                    grab: parseInt(selectedGrabValue),
                    dub: selectedDubValue,
                    score: currentTrick.value
                }
            }
            this.$emit('update-selected-tricks', trickEditIndex, trickForEdit);

        },
        cancelSelectedTrickEdit() {
            this.resetSelectedVariables();
            this.showResults = false;
            this.isActive = false;
            this.$emit('cancel-trick-edit');
        },
        editValidatedTrick(trickValidatedEditIndex,trickValidatedEditSubIndex,currentTrick, selectedRotationValue, selectedGrabValue, selectedDubValue) {
            let trickForEdit = this.validatedTricks[trickValidatedEditIndex] || null;
            let trickInCombo = trickForEdit && trickForEdit.body ? trickForEdit.body[trickValidatedEditSubIndex] : null;
            if (trickForEdit && !trickInCombo){
                trickForEdit = {
                    id: currentTrick.id,
                    name: currentTrick.name,
                    short_name: currentTrick.short_name,
                    category: currentTrick.categorie_id,
                    type: currentTrick.type_id,
                    level: currentTrick.level_id,
                    creator: currentTrick.creator,
                    value: currentTrick.value,
                    variation: selectedRotationValue,
                    grab: parseInt(selectedGrabValue),
                    dub: selectedDubValue,
                    score: currentTrick.value
                }
            }else if (trickInCombo){
                trickForEdit = {
                    id: currentTrick.id,
                    combo_id: trickInCombo.combo_id,
                    name: currentTrick.name,
                    short_name: currentTrick.short_name,
                    category: currentTrick.categorie_id,
                    type: currentTrick.type_id,
                    level: currentTrick.level_id,
                    creator: currentTrick.creator,
                    value: currentTrick.value,
                    variation: selectedRotationValue,
                    grab: parseInt(selectedGrabValue),
                    dub: selectedDubValue,
                    score: currentTrick.value
                }
            }
            this.$emit('update-validated-tricks', trickValidatedEditIndex, trickValidatedEditSubIndex, trickForEdit);

        },
        resetSelectedVariables() {
            this.selectedTrickId = null;
            this.selectedTrick = null;
            this.selectedOptionRotation = 0;
            this.selectedRotation = '';
            this.selectedGrab = '';
            this.selectedDub = '';
            this.isAthleteResultsMode = false;
        },
        updateDropdownHeight() {
            const selectedBox = document.getElementById('cardSelectedTutorial');
            const validatedBox = document.getElementById('cardValidatedTutorial');

            const selectedHeight = selectedBox ? selectedBox.getBoundingClientRect().height : 0;
            const validatedHeight = validatedBox ? validatedBox.getBoundingClientRect().height : 0;

            const desired = Math.max(520, Math.ceil(selectedHeight + validatedHeight));
            this.dropdownMinHeight = desired;
        },
    },
    watch: {
        selectedOption(newValue, oldValue) {
            if (newValue === oldValue) {
                this.selectedOption = null;
            }
        },
        trickEdit: {
            handler(newValue) {
                if (newValue) {
                    this.selectTrick(newValue);
                    this.selectedGrab = newValue.grab || "";
                    this.selectedDub = newValue.dub || "";
                    this.selectedOptionRotation = newValue.variation || 0;

                }
            },
            immediate: true,
        },
        trickValidatedEdit: {
            handler(newValue) {
                if (newValue) {
                    this.selectTrick(newValue);
                    this.selectedGrab = newValue.grab || "";
                    this.selectedDub = newValue.dub || "";
                    this.selectedOptionRotation = newValue.variation || 0;

                }
            },
            immediate: true,
        },

    },
    components: {
        FontAwesomeIcon,
    }
}
</script>

<style scoped>
/*Search Input*/
form {
    margin: 0;
}
input {
    width: 100%;
    padding: 8px;
    font-size: 16px;
    border: 1px solid #ccc;
    border-radius: 4px;
}
.active-button {
    background-color: #007bff;
    color: white;
}
.disabled-btn {
    opacity: 0.7;
    cursor: not-allowed;
}
.disabled-icon {
    color: #c0c0c0;
    transform: scale(0.9);
    transition: transform 0.2s ease;
}
.prohibited-icon {
    color: red;
}
.visually-hidden {
    position: absolute !important;
    clip: rect(1px, 1px, 1px, 1px);
    padding: 0 !important;
    border: 0 !important;
    height: 1px !important;
    width: 1px !important;
    overflow: hidden;
}
/*Result Box*/
.search-results {
    position: absolute;
    margin-top: 2px;
    top: calc(100% + 0px);
    left: 0;
    width: 100%;
    z-index: 200;
    background: rgba(227, 236, 255, 0.97);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    box-shadow: 0 14px 38px rgba(0, 0, 0, 0.16);
    min-height: 520px; /* altura generosa para cubrir por completo la caja inferior */
    max-height: 70vh;
    overflow-y: auto;
    backdrop-filter: blur(4px);
    animation: fadeInScale 120ms ease-out;
}
.search-results > div {
    padding: 12px;
    cursor: pointer;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.search-results-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 160px;
    text-align: center;
    color: #475569;
    font-weight: 600;
    cursor: default;
    white-space: normal;
}
.search-results > div:hover {
    background-color: rgba(28, 31, 27, 0.06);
}
.search-results-empty:hover {
    background-color: transparent;
}
@keyframes fadeInScale {
    from { opacity: 0; transform: translateY(-6px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}
.with-borders {
    border-top: 1px solid #000000;
    border-bottom: 1px solid #000000;
    border-right: 2px solid #000000;
    border-left: 2px solid #000000;
    padding: 0;
    text-align: center;
}
.color-level-1 {
    color: #ffffff;
    background-color: rgb(8, 76, 255);
}
.color-level-2 {
    color: #ffffff;
    background-color: rgb(23, 231, 0);
}
.color-level-3 {
    color: #ffffff;
    background-color: rgb(255, 126, 0);
}
.color-level-4 {
    color: #ffffff;
    background-color: rgb(255, 0, 0);
}
.level-circle {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1px solid #000;
    line-height: 7px;
}
.text-sm-general{
    font-size: 0.6rem;
    line-height: 10px;
}
.text-sm-alerts{
    font-size: 0.6rem;
}
.text-sm-score{
    font-size: 0.6rem;
    width: 17vw;
}
.text-sm-title{
    font-size: 0.7rem;
}
/*Score Buttons*/
select {
    width: 100%;
    padding: 8px;
    font-size: 16px;
}
.score-buttons {
    display: flex;
    justify-content: center;
    margin-top: 10px;
}
.check-icon {
    margin-right: 5px; /* Agregado: Espacio entre el ícono y el texto del botón */
}


@media (min-width: 450px) {
    .text-sm-general{
        font-size: 1rem;
        line-height: 25px;
    }
    .text-sm-alerts{
        font-size: 0.9rem;
    }
    .text-sm-score{
        font-size: 1rem;
        width: 10vw;
    }
    .text-sm-title{
        font-size: 1rem;
    }
    .level-circle {
        width: 30px;
        height: 30px;
        line-height: 25px;

    }
}
</style>